import { client } from '@api/rest/client';
import type { THotTopicOverviewItemDTO } from '@conversed/rest/src/contracts/train-ai/train-ai.dto';
import { HotTopicConversationFilter } from '@conversed/shared/src/central/constants/hot-topics';
import type { THotTopicMetadata } from '@conversed/shared/src/central/types/hot-topics.type';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import { toastInfoTranslated, toastSuccessTranslated } from '@helpers/toast';
import { useModal } from '@hooks/modal/useModal';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

type TUseHotTopicActionsParams = {
  onRefresh?: () => void;
};

// The two halves of the progress bar open the same review modal and differ only in which
// conversations are fetched and how the modal introduces itself.
const REVIEW_MODE_COPY: Record<
  | HotTopicConversationFilter.UNREVIEWED
  | HotTopicConversationFilter.PRE_APPROVED,
  { emptyMessageKey: string; titleKey: string }
> = {
  [HotTopicConversationFilter.UNREVIEWED]: {
    emptyMessageKey: 'train-ai.hot-topic-overview.all-caught-up',
    titleKey: 'train-ai.conversation-review.title',
  },
  [HotTopicConversationFilter.PRE_APPROVED]: {
    emptyMessageKey: 'train-ai.hot-topic-overview.no-pre-approved',
    titleKey: 'train-ai.conversation-review.pre-approved-title',
  },
};

export const useHotTopicActions = ({
  onRefresh,
}: TUseHotTopicActionsParams = {}) => {
  const dispatch = useDispatch();
  const { showModal, type: activeModalType } = useModal();
  const { t } = useTranslation();
  const wasReviewModalOpenedRef = useRef(false);

  // Refresh the overview when the review modal closes.
  // Detecting close via activeModalType rather than the modal's callback prop avoids
  // React Strict Mode's double-invoke of effect cleanups causing a premature refresh.
  useEffect(() => {
    if (wasReviewModalOpenedRef.current && !activeModalType) {
      wasReviewModalOpenedRef.current = false;
      onRefresh?.();
    }
  }, [activeModalType, onRefresh]);

  // Opens the review modal for one hot topic. `filter` decides which half of the progress
  // bar is loaded: UNREVIEWED for the Review button, PRE_APPROVED for the pre-approved
  // preview. Both use the same modal, so an already-approved answer can still be modified.
  const openConversationReview = useCallback(
    async (
      topic: THotTopicOverviewItemDTO,
      allTopics: THotTopicOverviewItemDTO[],
      filter: keyof typeof REVIEW_MODE_COPY,
    ): Promise<void> => {
      const { emptyMessageKey, titleKey } = REVIEW_MODE_COPY[filter];
      dispatch(mainActions.setUIState({ isLoading: true }));
      try {
        const { data } = await client.trainAI.getHotTopicConversations({
          query: {
            hotTopicId: topic.id,
            filter,
          },
        });

        if (data.totalRecords === 0) {
          toastInfoTranslated(emptyMessageKey);
          return;
        }

        wasReviewModalOpenedRef.current = true;
        showModal(
          'conversation-review',
          t(titleKey, { topicName: getHotTopicDisplayName(topic.name) }),
          {
            data,
            topic,
            requestedHotTopicId: topic.id,
            allTopics,
            isPreApprovedPreview:
              filter === HotTopicConversationFilter.PRE_APPROVED,
          },
        );
      } catch (error) {
        console.error('Failed to load conversations for hot topic:', error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, showModal, t],
  );

  const handleReview = useCallback(
    (
      topic: THotTopicOverviewItemDTO,
      allTopics: THotTopicOverviewItemDTO[],
    ): Promise<void> =>
      openConversationReview(
        topic,
        allTopics,
        HotTopicConversationFilter.UNREVIEWED,
      ),
    [openConversationReview],
  );

  const handleViewPreApproved = useCallback(
    (
      topic: THotTopicOverviewItemDTO,
      allTopics: THotTopicOverviewItemDTO[],
    ): Promise<void> =>
      openConversationReview(
        topic,
        allTopics,
        HotTopicConversationFilter.PRE_APPROVED,
      ),
    [openConversationReview],
  );

  // Fetch lightweight Q&A summaries for a hot topic, then open the related-questions modal.
  // Uses the summary endpoint (only _id, question, answer) to minimise payload size.
  // Uses the app-wide loading spinner so there is no modal-internal loading state.
  const handleViewRelatedConversations = useCallback(
    async (
      topicMetadata: THotTopicMetadata,
      onBack: () => void,
    ): Promise<void> => {
      dispatch(mainActions.setUIState({ isLoading: true }));
      try {
        const { data } = await client.trainAI.getHotTopicConversationSummaries({
          query: {
            hotTopicId: topicMetadata.id,
            filter: HotTopicConversationFilter.ALL,
          },
        });

        showModal('hot-topic-related-questions', '', {
          topic: { ...topicMetadata, conversations: data.results },
          hasBackNavigation: true,
          onBack,
        });
      } catch (error) {
        console.error(
          'Failed to load related conversations for hot topic:',
          error,
        );
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch, showModal],
  );

  const handleReassignConversationHotTopic = useCallback(
    async (
      conversationId: string,
      hotTopicId: string,
      topicName: string,
    ): Promise<boolean> => {
      try {
        await client.trainAI.setConversationHotTopic({
          params: { conversationId },
          body: { hotTopicId },
        });
        toastSuccessTranslated(
          'train-ai.conversation-review.stats.reassign-success',
          { topicName },
        );
        return true;
      } catch (error) {
        console.error('Failed to reassign hot topic:', error);
        return false;
      }
    },
    [],
  );

  return {
    handleReview,
    handleViewPreApproved,
    handleViewRelatedConversations,
    handleReassignConversationHotTopic,
  };
};
